import { useState, useEffect, useCallback } from 'react';
import { Fuel, AlertTriangle, TrendingDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const FuelPlanner = ({ routeCoords, routeDistance, vehicleProfile, c1, c2 }) => {
  const [fuelStations, setFuelStations] = useState([]);
  const [stationsAlongRoute, setStationsAlongRoute] = useState([]);

  const tank = vehicleProfile?.fuelTank || 60;
  const consumption = vehicleProfile?.consumption || 8; // L/100km
  const rangeKm = (tank / consumption) * 100;
  const routeKm = (routeDistance || 0) / 1000;
  const fuelNeeded = (routeKm * consumption) / 100;
  const fuelRemaining = tank - fuelNeeded;
  const isLowFuel = fuelNeeded > tank * 0.7;

  // Load cached fuel prices
  useEffect(() => {
    const cached = localStorage.getItem('crisis_fuel_prices');
    if (cached) {
      try { setFuelStations(JSON.parse(cached)); } catch {}
    }
    fetch(`${API}/api/offline-nav/fuel-prices`)
      .then(r => r.json())
      .then(data => {
        if (data.prices) {
          setFuelStations(data.prices);
          try { localStorage.setItem('crisis_fuel_prices', JSON.stringify(data.prices)); } catch {}
        }
      })
      .catch(() => {});
  }, []);

  // Find stations along route
  const findStationsAlongRoute = useCallback(() => {
    if (!routeCoords?.length || !fuelStations.length) return;
    const along = [];
    const searchRadius = 0.02; // ~2km in degrees

    for (const station of fuelStations) {
      if (!station.lat || !station.lng) continue;
      for (let i = 0; i < routeCoords.length; i += Math.max(1, Math.floor(routeCoords.length / 30))) {
        const cLat = routeCoords[i].lat || routeCoords[i][0];
        const cLng = routeCoords[i].lng || routeCoords[i][1];
        if (Math.abs(station.lat - cLat) < searchRadius && Math.abs(station.lng - cLng) < searchRadius) {
          along.push(station);
          break;
        }
      }
    }
    // Sort by price
    along.sort((a, b) => (a.unleaded || 999) - (b.unleaded || 999));
    setStationsAlongRoute(along.slice(0, 5));
  }, [routeCoords, fuelStations]);

  useEffect(() => { findStationsAlongRoute(); }, [findStationsAlongRoute]);

  if (!routeDistance || routeDistance < 1000) return null;

  return (
    <div className="rounded-lg border border-border/30 bg-card/80 p-2.5 space-y-2" data-testid="fuel-planner">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-2 font-semibold text-foreground">
          <Fuel className="w-3.5 h-3.5" style={{ color: c2 }} />
          Fuel Planning
        </span>
        {isLowFuel && (
          <span className="flex items-center gap-1 text-amber-400 text-[10px]">
            <AlertTriangle className="w-3 h-3" /> High fuel usage
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-md bg-muted/20 px-2 py-1.5">
          <p className="text-[10px] text-muted-foreground">Range</p>
          <p className="text-xs font-bold text-foreground">{Math.round(rangeKm)} km</p>
        </div>
        <div className="rounded-md bg-muted/20 px-2 py-1.5">
          <p className="text-[10px] text-muted-foreground">Fuel Needed</p>
          <p className="text-xs font-bold text-foreground">{fuelNeeded.toFixed(1)} L</p>
        </div>
        <div className={`rounded-md px-2 py-1.5 ${isLowFuel ? 'bg-amber-500/10' : 'bg-muted/20'}`}>
          <p className="text-[10px] text-muted-foreground">Remaining</p>
          <p className={`text-xs font-bold ${isLowFuel ? 'text-amber-400' : 'text-foreground'}`}>
            {fuelRemaining > 0 ? `${fuelRemaining.toFixed(1)} L` : 'Need refuel'}
          </p>
        </div>
      </div>
      {stationsAlongRoute.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] text-muted-foreground font-medium">Cheapest fuel along route:</p>
          {stationsAlongRoute.slice(0, 3).map((s, i) => (
            <div key={i} className="flex items-center justify-between text-[11px] rounded-md bg-muted/10 px-2 py-1">
              <span className="text-foreground/80 truncate max-w-[60%]">{s.brand} {s.address?.slice(0, 30)}</span>
              <div className="flex gap-2 text-muted-foreground">
                {s.unleaded && <span className="text-green-400 font-medium">{s.unleaded}p</span>}
                {s.diesel && <span className="text-blue-400 font-medium">{s.diesel}p</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {routeKm > rangeKm && (
        <div className="flex items-center gap-2 text-[11px] text-red-400 bg-red-500/10 rounded-md px-2 py-1.5">
          <TrendingDown className="w-3.5 h-3.5 flex-shrink-0" />
          Route exceeds fuel range. You'll need to refuel on the way.
        </div>
      )}
    </div>
  );
};
